'use client';

import { useState } from 'react';
import {
  MapPin,
  Calendar,
  Users,
  ChevronRight,
  HandHeart,
  ExternalLink,
  TreePine,
  Ticket,
  Play,
  X,
} from 'lucide-react';
import type { ChurchEvent, EventSession, CardTheme } from '@/types/serve-finder';
import { Img, useAssetUrl } from '@/lib/asset-url';
import { ServingRoleCard } from './ServingRoleCard';
import { RetreatFlow } from './RetreatFlow';
import { SessionPicker } from './SessionPicker';
import { OptionChooser } from './OptionChooser';
import { shortTime } from './EventHelpers';

interface EventDetailContentProps {
  event: ChurchEvent;
  theme: CardTheme | null;
  hasFullCardTheme: boolean;
  bodyStyle: React.CSSProperties;
  hasServing: boolean;
  hasSessions: boolean;
  hasSmartReg: boolean;
  hasOptions: boolean;
  hasServiceTimes: boolean;
  firstSessionWithTimes: EventSession | null | undefined;
  totalVolunteersNeeded: number;
  /** Callback to navigate to RSVP page (only available when inside a ResponsiveSheet) */
  onRSVP?: () => void;
  /** Callback to navigate to serve page (only available when inside a ResponsiveSheet) */
  onServe?: () => void;
}

export function EventDetailContent({
  event,
  theme,
  hasFullCardTheme,
  bodyStyle,
  hasServing,
  hasSessions,
  hasSmartReg,
  hasOptions,
  hasServiceTimes,
  firstSessionWithTimes,
  totalVolunteersNeeded,
  onRSVP,
  onServe,
}: EventDetailContentProps) {
  const [showVideo, setShowVideo] = useState(false);
  const resolve = useAssetUrl();

  // Extras from the first session with times (campus-filtered in production)
  const sessionExtras = firstSessionWithTimes?.Extras ?? [];

  if (hasServiceTimes) {
    return (
      <div className="min-h-[200px]" style={hasFullCardTheme ? undefined : bodyStyle}>
        {/* Description */}
        <div className="p-4">
          <div className="p-4" style={{ backgroundColor: 'rgba(23, 57, 45, 0.7)' }}>
            <p
              className="text-sm leading-relaxed"
              style={{ color: theme?.Body_Color ?? 'rgba(255,255,255,0.8)' }}
            >
              {event.Description}
            </p>
          </div>
        </div>

        {/* Consolidated service times — first session only (campus-filtered in production) */}
        {firstSessionWithTimes?.Service_Times &&
          (() => {
            const times = firstSessionWithTimes.Service_Times!;
            const grouped = times.reduce<Record<string, typeof times>>((acc, t) => {
              (acc[t.Day] ??= []).push(t);
              return acc;
            }, {});
            const days = Object.entries(grouped);

            return (
              <div className="px-4 pb-4">
                <div className="p-4" style={{ backgroundColor: 'rgba(23, 57, 45, 0.7)' }}>
                  <div className="mb-2 flex items-center gap-2.5">
                    <Img
                      src={`/${event.BG_URL.split('/')[1]}/${firstSessionWithTimes.Label.replace(/\s+/g, '')}.svg`}
                      alt=""
                      className="h-7 w-7"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <span
                      className="text-sm font-bold tracking-wide uppercase"
                      style={{ color: theme?.Title_Color ?? '#ffffff' }}
                    >
                      {firstSessionWithTimes.Label}
                    </span>
                  </div>
                  {days.map(([day, dayTimes]) => (
                    <div key={day} className="mb-1.5 last:mb-0">
                      <p
                        className="mb-0.5 text-[10px] font-semibold tracking-wider uppercase"
                        style={{ color: theme?.Body_Color ?? 'rgba(255,255,255,0.5)' }}
                      >
                        {day}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        {dayTimes.map((t) => (
                          <span
                            key={`${day}-${t.Time}`}
                            className="text-base font-extrabold"
                            style={{ color: theme?.Accent_Color ?? '#d4a08a' }}
                          >
                            {shortTime(t.Time)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        {/* RSVP — row button that pages to detailed times */}
        <div className="px-4 pb-4">
          <button
            type="button"
            onClick={() => onRSVP?.()}
            className="flex w-full items-center gap-3 p-3 text-left transition-shadow hover:shadow-md"
            style={{ backgroundColor: 'rgba(23, 57, 45, 0.7)' }}
          >
            <Ticket
              className="h-5 w-5 shrink-0"
              style={{ color: theme?.Accent_Color ?? '#d4a08a' }}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold" style={{ color: theme?.Title_Color ?? '#ffffff' }}>
                {event.Registration_Label ?? 'RSVP'}
              </p>
              <p
                className="mt-0.5 text-xs"
                style={{ color: theme?.Body_Color ?? 'rgba(255,255,255,0.7)' }}
              >
                Pick a campus &amp; time
              </p>
            </div>
            <ChevronRight
              className="h-4 w-4 shrink-0"
              style={{ color: theme?.Accent_Color ?? '#d4a08a' }}
            />
          </button>
        </div>

        {/* Extras */}
        {sessionExtras.length > 0 && (
          <div className="space-y-2 px-4 pb-4">
            {sessionExtras.map((extra) => (
              <a
                key={extra.Label}
                href={extra.URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3"
                style={{ backgroundColor: 'rgba(23, 57, 45, 0.7)' }}
              >
                {extra.Image_URL && (
                  <div className="h-12 w-20 shrink-0 overflow-hidden">
                    <Img src={extra.Image_URL} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-bold"
                    style={{ color: theme?.Title_Color ?? '#ffffff' }}
                  >
                    {extra.Label}
                  </p>
                  <p
                    className="mt-0.5 text-xs"
                    style={{ color: theme?.Body_Color ?? 'rgba(255,255,255,0.7)' }}
                  >
                    {extra.Description}
                  </p>
                </div>
                <ExternalLink
                  className="h-3.5 w-3.5 shrink-0"
                  style={{ color: theme?.Accent_Color ?? '#d4a08a' }}
                />
              </a>
            ))}
          </div>
        )}

        {hasServing && (
          <div className="px-4 pb-4">
            <div className="p-4" style={{ backgroundColor: 'rgba(23, 57, 45, 0.7)' }}>
              <div className="mb-3 flex items-center gap-2">
                <HandHeart
                  className="h-4 w-4"
                  style={{ color: theme?.Accent_Color ?? '#d4a08a' }}
                />
                <p
                  className="text-xs font-semibold tracking-wider uppercase"
                  style={{ color: theme?.Body_Color ?? 'rgba(255,255,255,0.8)' }}
                >
                  Serve at this event
                </p>
              </div>
              <div className="space-y-3">
                {event.Serving_Roles.map((role) => (
                  <ServingRoleCard key={role.ID} role={role} />
                ))}
              </div>
            </div>
          </div>
        )}

        {event.Page_URL && (
          <a
            href={event.Page_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide uppercase transition-opacity hover:opacity-80"
            style={{
              color: theme?.Accent_Color ?? '#d4a08a',
              borderColor: theme?.Border_Color ?? 'rgba(255,255,255,0.1)',
            }}
          >
            <TreePine className="h-4 w-4" />
            Learn More
            <ChevronRight className="h-4 w-4" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-[200px]" style={hasFullCardTheme ? undefined : bodyStyle}>
      <div className="space-y-5 p-4">
        {event.Detail_Heading ? (
          <div
            className="overflow-hidden p-5"
            style={{ backgroundColor: event.Detail_BG ?? '#0A9B98' }}
          >
            <div className={event.Detail_Image_URL ? 'flex flex-col gap-4 sm:flex-row' : ''}>
              <div className="min-w-0 flex-1">
                <h2
                  className="text-3xl leading-[0.9] font-black whitespace-pre-line uppercase"
                  style={{
                    color: event.Detail_Color ?? '#E7DBC2',
                    letterSpacing: event.Detail_Heading_Tracking,
                  }}
                >
                  {event.Detail_Heading}
                </h2>
                <p
                  className="mt-3 text-sm leading-relaxed"
                  style={{
                    color: event.Detail_Body_Color ?? theme?.Body_Color ?? 'rgba(255,255,255,0.8)',
                  }}
                >
                  {event.Description}
                </p>
              </div>

              {event.Detail_Image_URL && event.Detail_Video_URL && (
                <button
                  type="button"
                  onClick={() => setShowVideo(true)}
                  className="group relative w-full shrink-0 self-center overflow-hidden sm:w-36"
                >
                  <Img
                    src={event.Detail_Image_URL}
                    alt="Watch video"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110">
                      <Play className="h-4 w-4 fill-current text-black" style={{ marginLeft: 1 }} />
                    </div>
                  </div>
                </button>
              )}

              {event.Detail_Image_URL && !event.Detail_Video_URL && (
                <div className="w-full shrink-0 overflow-hidden sm:w-44 sm:self-center">
                  <Img src={event.Detail_Image_URL} alt="" className="h-full w-full object-cover" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm leading-relaxed" style={{ color: theme?.Body_Color ?? undefined }}>
            <span className={theme ? '' : 'text-muted-foreground'}>{event.Description}</span>
          </p>
        )}

        {hasSmartReg && <RetreatFlow registration={event.Smart_Registration!} />}

        {hasOptions && (
          <OptionChooser
            options={event.Registration_Options!}
            accentColor={theme?.Accent_Color}
            onServe={onServe}
          />
        )}

        {event.Registration_URL && !hasSmartReg && !hasOptions && (
          <a
            href={event.Registration_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 px-5 py-3 text-sm font-bold tracking-wide text-white uppercase transition-opacity hover:opacity-90 active:scale-[0.98]"
            style={{ backgroundColor: theme?.Accent_Color ?? 'var(--brand-secondary)' }}
          >
            {event.Registration_Label ?? 'Register'}
            <ExternalLink className="h-4 w-4" />
          </a>
        )}

        {hasSessions && !hasSmartReg && !hasServiceTimes && (
          <SessionPicker
            sessions={event.Sessions}
            registrationLabel={event.Registration_Label ?? 'Register'}
          />
        )}

        {hasServing && !onServe && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HandHeart
                  className="h-4 w-4"
                  style={{ color: theme?.Accent_Color ?? 'var(--brand-secondary)' }}
                />
                <p
                  className="text-xs font-semibold tracking-wider uppercase"
                  style={{ color: theme?.Body_Color ?? undefined }}
                >
                  <span className={theme ? '' : 'text-muted-foreground'}>Serve at this event</span>
                </p>
              </div>
              {totalVolunteersNeeded > 0 && (
                <p
                  className="flex items-center gap-1 text-xs font-medium"
                  style={{ color: theme?.Accent_Color ?? undefined }}
                >
                  <span className={theme ? '' : 'text-[var(--brand-secondary)]'}>
                    <Users className="inline h-3.5 w-3.5" />
                  </span>
                  <span className={theme ? '' : 'text-[var(--brand-secondary)]'}>
                    {totalVolunteersNeeded} needed
                  </span>
                </p>
              )}
            </div>

            <div className="space-y-3">
              {event.Serving_Roles.map((role) => (
                <ServingRoleCard key={role.ID} role={role} />
              ))}
            </div>
          </div>
        )}
      </div>

      {event.Page_URL && (
        <a
          href={event.Page_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide uppercase transition-opacity hover:opacity-80"
          style={{
            color: theme?.Accent_Color ?? 'var(--brand-secondary)',
            borderColor: theme?.Border_Color ?? 'var(--border)',
          }}
        >
          Learn More
          <ChevronRight className="h-4 w-4" />
        </a>
      )}

      {/* Video overlay */}
      {showVideo && event.Detail_Video_URL && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setShowVideo(false)}
        >
          <button
            type="button"
            onClick={() => setShowVideo(false)}
            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="w-full max-w-2xl px-4" onClick={(e) => e.stopPropagation()}>
            <video
              src={resolve(event.Detail_Video_URL)}
              controls
              autoPlay
              playsInline
              className="w-full rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
